import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LuHistory, LuPlus } from 'react-icons/lu';
import useChatStore from '../store/chatStore';
import useSchoolStore from '../store/schoolStore';
import useUserStore from '../store/userStore';
import ChatMessage from '../components/chat/ChatMessage';
import InputBox from '../components/chat/InputBox';
import AskMeStart from '../components/chat/AskMeStart';
import RecentChats from '../components/chat/RecentChats';
import QuickCommandDialog from '../components/chat/QuickCommandDialog';
import Button from '../components/common/Button';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingMessage from '../components/common/LoadingMessage';
import { useToast } from '../components/common/Toast';
import { STRINGS } from '../constants/strings';

/**
 * Ask Me: questions about the school answered by the AI agent.
 * A question typed in the top bar (AskMeBar) arrives as `location.state.question` and starts
 * a new conversation; a ready-made question picked on Home arrives as `location.state.commandId`.
 * Past conversations are in the "Recent questions" panel, closed by default.
 */
const AskMePage = () => {
  const [currentMessage, setCurrentMessage] = useState('');
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [chatIndexToDelete, setChatIndexToDelete] = useState(null);
  const [pickedCommand, setPickedCommand] = useState(null);
  const { schoolData } = useSchoolStore();
  const { getPublicUserProfile } = useUserStore();
  const { showToast, ToastComponent } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const handledQuestionRef = useRef(null);

  const {
    chatList,
    currentChatMessages,
    isLoading,
    isLoadingChatHistory,
    isClearingChatSession,
    sendStreamingMessage,
    getChatHistory,
    deleteChatList,
    error,
    selectedChatIndex,
    setSelectedChatIndex,
    setSelectedSessionId,
  } = useChatStore();

  // Show chat errors (subscription, sending, deleting) as a toast
  useEffect(() => {
    if (error) {
      showToast(error.message, 'error');
    }
  }, [error, showToast]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [currentChatMessages, isLoading]);

  const sendQuestion = async (question) => {
    if (!question.trim()) return;

    // Safe public user profile for the AI service (useUserStore.getPublicUserProfile)
    const publicProfileResult = getPublicUserProfile();
    const userInfo = publicProfileResult.success ? publicProfileResult.value : null;

    await sendStreamingMessage({
      schoolInfo: schoolData,
      userInfo: userInfo,
      prompt: question,
      noCache: true
    });
  };

  // Clears the selection so the next question starts a new conversation (useChatStore.sendStreamingMessage)
  const startNewConversation = () => {
    setSelectedChatIndex(chatList.length);
    setSelectedSessionId(null);
  };

  // Handed over from another page, once per navigation:
  // a question typed in the top bar starts a new conversation with it;
  // a ready-made question picked on Home opens its form (QuickCommandDialog).
  useEffect(() => {
    const question = location.state?.question;
    const commandId = location.state?.commandId;
    if ((!question && !commandId) || handledQuestionRef.current === location.key) {
      return;
    }

    handledQuestionRef.current = location.key;
    navigate(location.pathname, { replace: true, state: null });
    startNewConversation();

    if (commandId) {
      const command = STRINGS.ASKME.COMMANDS.find((commandItem) => commandItem.id === commandId);
      setPickedCommand(command || null);
      return;
    }

    sendQuestion(question);
    // Runs once per navigation: the handover is keyed on location.key, not on the helpers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const handleSendMessage = () => {
    const question = currentMessage;
    setCurrentMessage('');
    sendQuestion(question);
  };

  const handleNewChat = () => {
    startNewConversation();
    setCurrentMessage('');
  };

  const handleSelectChat = async (chatIndex) => {
    // On narrow screens the panel covers the conversation, so it closes once one is picked
    if (window.innerWidth < 1024) {
      setIsHistoryOpen(false);
    }
    setSelectedChatIndex(chatIndex);
    const selectedChat = chatList[chatIndex];
    if (selectedChat && selectedChat.sessionId) {
      await getChatHistory(selectedChat.sessionId);
    }
  };

  const handleDeleteChat = (event, chatIndex) => {
    event.stopPropagation();
    setChatIndexToDelete(chatIndex);
  };

  const handleConfirmDelete = () => {
    deleteChatList(chatIndexToDelete);
    setChatIndexToDelete(null);
  };

  const hasMessages = selectedChatIndex !== null && currentChatMessages.length > 0;
  const conversationTitle = selectedChatIndex !== null && chatList[selectedChatIndex]
    ? String(chatList[selectedChatIndex].title || chatList[selectedChatIndex].lastMessage?.content || '').replace(/[#*_`>]/g, '').trim()
    : '';

  const renderConversation = () => {
    if (isLoadingChatHistory) {
      return <LoadingMessage message="Opening the conversation…" className="h-full" />;
    }

    if (!hasMessages) {
      return <AskMeStart onPickCommand={setPickedCommand} />;
    }

    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 px-6 py-6">
        {currentChatMessages.map((message, messageIndex) => (
          <div
            key={messageIndex}
            className={`flex animate-enter ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <ChatMessage message={message} />
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
    );
  };

  return (
    <div className="relative flex h-full">
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex h-12 shrink-0 items-center justify-between gap-4 border-b border-ui-line bg-ui-card px-6">
          <div className="flex min-w-0 items-baseline gap-3">
            <h1 className="text-base font-semibold text-ui-ink">Ask Me</h1>
            {hasMessages && conversationTitle && (
              <p className="truncate text-sm text-ui-ink-3">{conversationTitle}</p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {hasMessages && (
              <Button variant="secondary" size="sm" leftIcon={<LuPlus className="size-4" />} onClick={handleNewChat}>
                New question
              </Button>
            )}
            <Button
              variant={isHistoryOpen ? 'secondary' : 'ghost'}
              size="sm"
              leftIcon={<LuHistory className="size-4" />}
              onClick={() => setIsHistoryOpen(!isHistoryOpen)}
              aria-expanded={isHistoryOpen}
            >
              Recent questions
            </Button>
          </div>
        </div>

        <div className="ui-scroll min-h-0 flex-1 overflow-y-auto">{renderConversation()}</div>

        <div className="shrink-0 border-t border-ui-line bg-ui-card px-6 py-3">
          <div className="mx-auto max-w-3xl">
            <InputBox
              value={currentMessage}
              onChange={setCurrentMessage}
              onSendMessage={handleSendMessage}
              isLoading={isLoading}
              placeholder={hasMessages ? 'Ask a follow-up question…' : 'Ask a question about students, classes, attendance or exams…'}
            />
            <p className="mt-1.5 text-center text-[11px] text-ui-ink-3">
              Answers are prepared by AI from your school’s data. Check important figures before sharing them.
            </p>
          </div>
        </div>
      </div>

      {isHistoryOpen && (
        <RecentChats
          chatList={chatList}
          selectedChatIndex={selectedChatIndex}
          isDeleting={isClearingChatSession}
          onSelectChat={handleSelectChat}
          onDeleteChat={handleDeleteChat}
          onClose={() => setIsHistoryOpen(false)}
        />
      )}

      <QuickCommandDialog
        isOpen={pickedCommand !== null}
        command={pickedCommand}
        onClose={() => setPickedCommand(null)}
        onSubmit={(finalCommand) => setCurrentMessage(finalCommand)}
      />

      <ConfirmDialog
        isOpen={chatIndexToDelete !== null}
        title="Delete this conversation?"
        message="The questions and answers in it will be removed. This cannot be undone."
        confirmLabel="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setChatIndexToDelete(null)}
      />

      {ToastComponent}
    </div>
  );
};

export default AskMePage;
